/**
 * Invoice Page - 3 Tabs (AWS-style)
 * Tab 1: Charges by service (with regional breakdown)
 * Tab 2: Charges by account
 * Tab 3: Chargeback (department-wise cost allocation)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileSpreadsheet, Printer, RefreshCw } from 'lucide-react';
import { formatCurrency, CurrencyCode } from '../utils/currency';
import { getActiveTenantId } from '../services/tenantContext';
import { Button } from '../components/common/Button';
import { PillTabs } from '../components/common/Modal';
import { ChargebackDrilldown } from '../components/invoice/ChargebackDrilldown';
import { DepartmentStatement } from '../components/invoice/DepartmentStatement';

const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

// tenantContextMiddleware requires a verified Bearer token now - tenant
// headers alone are no longer sufficient, so this must read the current
// token fresh on every call rather than being a static object.
// forceFresh adds the standard Cache-Control: no-cache, which the cost API
// honours by skipping its read cache (see shouldBypassCache). Cost responses
// are cached server-side for an hour, so without this the Refresh button
// re-fetched and got the identical cached payload - it looked broken because
// functionally it was.
function getHeaders(forceFresh = false): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    'x-user-id': 'user-123',
    ...(forceFresh ? { 'Cache-Control': 'no-cache' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface ServiceCost {
  service: string;
  totalCost: number;
  regions: { region: string; cost: number }[];
  expanded?: boolean;
}

interface AccountCost {
  account: string;
  accountName: string;
  provider: string;
  totalCost: number;
}

export const InvoicePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'service' | 'account' | 'chargeback'>('service');
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  // Which department's printable statement is open, if any.
  const [printDepartment, setPrintDepartment] = useState<string | null>(null);
  
  // Data
  const [serviceData, setServiceData] = useState<ServiceCost[]>([]);
  const [accountData, setAccountData] = useState<AccountCost[]>([]);
  const [chargebackData, setChargebackData] = useState<any>(null);
  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Expand/collapse state
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

  // Generate last 12 months
  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedProvider]);

  // Chargeback used to be hidden for Azure/GCP because its data came from a
  // hand-written AWS Athena query. The drill-down runs on the shared
  // multi-provider cost layer instead, so the tab is valid for every provider
  // and no longer needs resetting when the provider changes.

  const fetchData = async (forceFresh = false) => {
    if (forceFresh) setRefreshing(true); else setLoading(true);
    setError(null);
    
    try {
      // Parse month to get start and end dates
      const [year, month] = selectedMonth.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of month
      
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      // Build filter object for provider (not as query string!)
      const filter: any = {};
      if (selectedProvider !== 'all') {
        filter.provider = selectedProvider;
      }
      const filterParam = Object.keys(filter).length > 0 ? JSON.stringify(filter) : undefined;

      // Fetch cost data grouped by service and region using proper array format
      const servicePromise = axios.get(
        `${COST_API_URL}/cost`,
        { 
          headers: getHeaders(forceFresh), 
          timeout: 60000,
          params: {
            start: startStr,
            end: endStr,
            group_by: 'service,region',  // Validation middleware will split this
            // NET UNBLENDED - the billed amount, after discounts and credits.
            //
            // This was 'amortized', which is wrong for an invoice on purpose
            // rather than by accident: amortization spreads Savings Plan and
            // Reserved Instance UPFRONT payments across the commitment term,
            // so it deliberately does not equal the amount charged in any one
            // month. Blended is likewise an averaged rate across on-demand and
            // committed usage - an accounting view, never a billed figure.
            // Only net unblended reconciles to the invoice AWS actually sends.
            cost_view: 'net_unblended',
            page_size: 1000,
            ...(filterParam && { filter: filterParam })
          }
        }
      ).catch((err) => {
        console.error('Service data fetch error:', err);
        return { data: { data: [] } };
      });

      // Fetch cost data grouped by account
      const accountPromise = axios.get(
        `${COST_API_URL}/cost`,
        { 
          headers: getHeaders(forceFresh), 
          timeout: 60000,
          params: {
            start: startStr,
            end: endStr,
            group_by: 'account',
            // Same basis as the service/region query above - the two must
            // agree, or the per-account totals will not sum to the invoice.
            cost_view: 'net_unblended',
            page_size: 1000,
            ...(filterParam && { filter: filterParam })
          }
        }
      ).catch((err) => {
        console.error('Account data fetch error:', err);
        return { data: { data: [] } };
      });

      // Fetch chargeback data (for Tab 3)
      const chargebackPromise = axios.get(
        `${COST_API_URL}/tagging/chargeback`,
        { headers: getHeaders(forceFresh), timeout: 60000 }
      ).catch(() => ({ data: { data: null } }));

      const [serviceRes, accountRes, chargebackRes] = await Promise.all([
        servicePromise,
        accountPromise,
        chargebackPromise,
      ]);

      console.log('Service API response:', serviceRes.data);
      console.log('Account API response:', accountRes.data);
      
      // Log first service row for debugging
      if (serviceRes.data.data && serviceRes.data.data.length > 0) {
        console.log('First service row:', JSON.stringify(serviceRes.data.data[0], null, 2));
      }

      // Process service data - group by service, then by region
      const serviceMap = new Map<string, { totalCost: number; regions: Map<string, number> }>();

      const serviceRows = serviceRes.data.data || [];

      // Currency comes from the backend per the selected provider filter:
      // Azure-only -> INR, AWS-only / All Providers -> USD
      const rowCurrency: CurrencyCode = serviceRows[0]?.currency === 'INR' ? 'INR' : 'USD';
      setCurrency(rowCurrency);
      for (const row of serviceRows) {
        const service = row.dimensions?.service || row.service || 'Unknown Service';
        const region = row.dimensions?.region || row.region || 'global';
        const cost = row.cost || row.totalCost || 0;
        
        console.log('Processing service row:', { service, region, cost, row });
        
        if (!serviceMap.has(service)) {
          serviceMap.set(service, { totalCost: 0, regions: new Map() });
        }
        
        const svc = serviceMap.get(service)!;
        svc.totalCost += cost;
        svc.regions.set(region, (svc.regions.get(region) || 0) + cost);
      }
      
      const services: ServiceCost[] = Array.from(serviceMap.entries())
        .map(([service, data]) => ({
          service,
          totalCost: data.totalCost,
          regions: Array.from(data.regions.entries())
            .map(([region, cost]) => ({ region, cost }))
            .sort((a, b) => b.cost - a.cost),
        }))
        .sort((a, b) => b.totalCost - a.totalCost);

      setServiceData(services);

      // Process account data - aggregate by account ID (API returns per-day rows)
      const accountRows = accountRes.data.data || [];
      const accountMap = new Map<string, AccountCost>();
      for (const row of accountRows) {
        const account = row.dimensions?.account || row.account || 'Unknown';
        const cost = row.cost || row.totalCost || 0;
        const existing = accountMap.get(account);
        if (existing) {
          existing.totalCost += cost;
        } else {
          accountMap.set(account, {
            account,
            accountName: row.accountName || `Account ${account}`,
            provider: row.dimensions?.provider || row.provider || 'aws',
            totalCost: cost,
          });
        }
      }
      const accounts: AccountCost[] = Array.from(accountMap.values())
        .sort((a, b) => b.totalCost - a.totalCost);

      setAccountData(accounts);
      setChargebackData(chargebackRes.data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load invoice data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleService = (service: string) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(service)) {
      newExpanded.delete(service);
    } else {
      newExpanded.add(service);
    }
    setExpandedServices(newExpanded);
  };

  const expandAll = () => {
    setExpandedServices(new Set(serviceData.map(s => s.service)));
  };

  const collapseAll = () => {
    setExpandedServices(new Set());
  };

  const exportCSV = () => {
    let csvContent = '';
    const providerLabel = selectedProvider === 'all' ? 'All' : selectedProvider.toUpperCase();

    if (activeTab === 'service') {
      csvContent = 'Service,Region,Cost\n';
      for (const svc of serviceData) {
        csvContent += `${svc.service},All Regions,${svc.totalCost.toFixed(2)}\n`;
        for (const reg of svc.regions) {
          csvContent += `${svc.service},${reg.region},${reg.cost.toFixed(2)}\n`;
        }
      }
      const filename = `Invoice-Services-${providerLabel}-${selectedMonth}.csv`;
      downloadCSV(csvContent, filename);
    } else if (activeTab === 'account') {
      csvContent = 'Account,Account Name,Provider,Cost\n';
      for (const acc of accountData) {
        csvContent += `${acc.account},${acc.accountName},${acc.provider.toUpperCase()},${acc.totalCost.toFixed(2)}\n`;
      }
      const filename = `Invoice-Accounts-${providerLabel}-${selectedMonth}.csv`;
      downloadCSV(csvContent, filename);
    } else if (activeTab === 'chargeback' && chargebackData) {
      csvContent = 'Department,Cost Center,Head,Cost\n';
      for (const dept of chargebackData.departments || []) {
        csvContent += `${dept.name},${dept.costCenter},${dept.head},${dept.totalCost.toFixed(2)}\n`;
      }
      const filename = `Invoice-Chargeback-${selectedMonth}.csv`;
      downloadCSV(csvContent, filename);
    }
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const printInvoice = () => {
    window.print();
  };

  const totalInvoiceAmount = activeTab === 'service' 
    ? serviceData.reduce((sum, s) => sum + s.totalCost, 0)
    : activeTab === 'account'
    ? accountData.reduce((sum, a) => sum + a.totalCost, 0)
    : chargebackData?.summary?.totalCost || 0;

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-end gap-2 print:hidden">
        <div className="flex gap-2">
          <Button onClick={exportCSV} variant="success" size="sm" title="Export to CSV">
            <FileSpreadsheet /> Export CSV
          </Button>
          <Button onClick={printInvoice} variant="outline" size="sm" title="Print invoice">
            <Printer /> Print
          </Button>
          <Button onClick={() => fetchData(true)} disabled={refreshing} size="sm">
            <RefreshCw className={refreshing ? "animate-spin" : undefined} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow p-4 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full h-9 border border-border rounded-md px-3 py-0 text-sm leading-none focus:ring-2 focus:ring-ring focus:border-ring"
            >
              {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">Cloud Provider</label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="w-full h-9 border border-border rounded-md px-3 py-0 text-sm leading-none focus:ring-2 focus:ring-ring focus:border-ring"
            >
              <option value="all">All Providers</option>
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
              <option value="gcp">GCP</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="print:hidden">
        <PillTabs
          tabs={[
            { key: 'service', label: 'Charges by service' },
            { key: 'account', label: 'Charges by account' },
            { key: 'chargeback', label: 'Chargeback' },
          ]}
          active={activeTab}
          onChange={(k) => setActiveTab(k as typeof activeTab)}
        />
      </div>

      {loading && <div className="text-center py-12 text-muted-foreground">Loading invoice data...</div>}
      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg">{error}</div>}

      {/* TAB 1: Charges by service */}
      {!loading && activeTab === 'service' && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/10 rounded-lg shadow p-6 border-l-4 border-primary/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary font-medium">Total Invoice Amount</p>
                <p className="text-4xl font-bold text-primary mt-1">{formatCurrency(totalInvoiceAmount, currency)}</p>
                <p className="text-xs text-primary mt-1">
                  {monthOptions.find(m => m.value === selectedMonth)?.label} •
                  {selectedProvider === 'all' ? ' All Providers' : ` ${selectedProvider.toUpperCase()}`}
                </p>
                {/* Name the cost basis. A figure labelled only "Total Invoice
                    Amount" gets reconciled against a real bill by someone who
                    has no way to know whether it is list price, an averaged
                    rate or the billed amount - and all three were plausible
                    here until recently. */}
                <p className="text-xs text-muted-foreground mt-1">
                  Net cost — after discounts and credits
                </p>
              </div>
              <div className="text-right print:hidden">
                <button onClick={expandAll} className="text-sm text-primary hover:text-primary mr-3">
                  ▼ Expand All
                </button>
                <button onClick={collapseAll} className="text-sm text-primary hover:text-primary">
                  ▲ Collapse All
                </button>
              </div>
            </div>
          </div>

          {/* Services Table */}
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {serviceData.map((svc) => (
                  <React.Fragment key={svc.service}>
                    <tr className="hover:bg-muted/30 cursor-pointer" onClick={() => toggleService(svc.service)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-muted-foreground/70 mr-2 print:hidden">
                            {expandedServices.has(svc.service) ? '▼' : '▶'}
                          </span>
                          <span className="text-sm font-medium text-foreground">{svc.service}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-foreground">
                        {formatCurrency(svc.totalCost, currency)}
                      </td>
                    </tr>
                    {expandedServices.has(svc.service) && svc.regions.map((reg) => (
                      <tr key={`${svc.service}-${reg.region}`} className="bg-primary/10">
                        <td className="px-6 py-3 whitespace-nowrap pl-16">
                          <span className="text-xs text-muted-foreground">📍 {reg.region}</span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-xs text-foreground/80">
                          {formatCurrency(reg.cost, currency)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {serviceData.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-muted-foreground">
                      No service data available for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-secondary">
                <tr>
                  <td className="px-6 py-4 text-sm font-bold text-foreground">Total</td>
                  <td className="px-6 py-4 text-right text-sm font-bold text-foreground">
                    {formatCurrency(totalInvoiceAmount, currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Charges by account */}
      {!loading && activeTab === 'account' && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-sky-50 to-sky-100 rounded-lg shadow p-6 border-l-4 border-sky-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-800 font-medium">Total Invoice Amount</p>
                <p className="text-4xl font-bold text-sky-900 mt-1">{formatCurrency(totalInvoiceAmount, currency)}</p>
                <p className="text-xs text-sky-600 mt-1">
                  {monthOptions.find(m => m.value === selectedMonth)?.label} • 
                  {selectedProvider === 'all' ? ' All Providers' : ` ${selectedProvider.toUpperCase()}`}
                </p>
              </div>
            </div>
          </div>

          {/* Accounts Table */}
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {accountData.map((acc) => (
                  <tr key={acc.account} className="hover:bg-muted/30">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      {acc.account}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {acc.accountName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-secondary rounded uppercase font-medium">
                        {acc.provider}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-foreground">
                      {formatCurrency(acc.totalCost, currency)}
                    </td>
                  </tr>
                ))}
                {accountData.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                      No account data available for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-secondary">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm font-bold text-foreground">Total</td>
                  <td className="px-6 py-4 text-right text-sm font-bold text-foreground">
                    {formatCurrency(totalInvoiceAmount, currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: Chargeback */}
      {!loading && activeTab === 'chargeback' && chargebackData && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg shadow p-6 border-l-4 border-emerald-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-emerald-800 font-medium">Total Cost</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">${chargebackData.summary.totalCost.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-emerald-800 font-medium">Allocated to Departments</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">${chargebackData.summary.allocatedCost.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-emerald-800 font-medium">Unallocated (Shared)</p>
                <p className="text-3xl font-bold text-rose-600 mt-1">${chargebackData.summary.unallocatedCost.toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Departments Table */}
          {/* Drill-down replaces the flat department table: the question a
              department head actually asks is "which of MY accounts/services
              drove this", which a single-level table cannot answer. Each level
              loads on expand - see ChargebackDrilldown for why the tree is not
              pre-built. */}
          <ChargebackDrilldown
            apiBase={COST_API_URL}
            authHeaders={getHeaders}
            month={selectedMonth}
            provider={selectedProvider}
            onPrintDepartment={setPrintDepartment}
          />

          {/* Unallocated warning */}
          {chargebackData.unallocated && chargebackData.unallocated.totalCost > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-rose-800">⚠️ Unallocated Costs</p>
                  <p className="text-xs text-rose-600 mt-1">
                    ${chargebackData.unallocated.totalCost.toFixed(2)} in costs cannot be charged back due to missing Department tags
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {printDepartment && (
        <DepartmentStatement
          apiBase={COST_API_URL}
          authHeaders={getHeaders}
          department={printDepartment}
          month={selectedMonth}
          provider={selectedProvider}
          onClose={() => setPrintDepartment(null)}
        />
      )}
    </div>
  );
};

export default InvoicePage;
